import * as React from 'react'
import { useState } from 'react'
import styled from 'styled-components'
import { screenSizes } from '@src/styles/theme'
import { ConnectionStatus } from '../types'

export interface ConnectionCardProps {
  connectionStatus: ConnectionStatus
  gatewayUrl: string
  token: string
  password: string
  errorMessage: string | null
  setGatewayUrl: (url: string) => void
  setToken: (token: string) => void
  setPassword: (password: string) => void
  connect: () => void
  disconnect: () => void
}

const Card = styled.div`
  background-color: ${(p) => p.theme.secondaryPanel};
  border-radius: ${(p) => p.theme.radius};
  padding: 20px;
  margin-bottom: 20px;

  @media (max-width: ${screenSizes.XS}px) {
    padding: 16px;
  }
`

const CardHeader = styled.div<{ $clickable?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
  user-select: none;
  flex-wrap: wrap;
`

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`

const StatusDot = styled.span<{ $status: ConnectionStatus }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${(p) => {
    switch (p.$status) {
      case 'connected':
        return p.theme.accent
      case 'connecting':
        return p.theme.warning
      case 'error':
        return p.theme.error
      default:
        return p.theme.gray
    }
  }};
  flex-shrink: 0;
`

const PulsingDot = styled.span<{ $status: ConnectionStatus }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${(p) => {
    switch (p.$status) {
      case 'connected':
        return p.theme.accent
      case 'connecting':
        return p.theme.warning
      case 'error':
        return p.theme.error
      default:
        return p.theme.gray
    }
  }};
  flex-shrink: 0;
  animation: pulse 1.4s ease-in-out infinite;
  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.4;
    }
  }
`

const StatusLabel = styled.span`
  font-size: 14px;
  color: ${(p) => p.theme.white};
  font-family: 'NunitoSansSemiBold';
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: ${screenSizes.XS}px) {
    white-space: normal;
    word-break: break-word;
  }
`

const ExpandIcon = styled.span<{ $expanded: boolean }>`
  font-size: 12px;
  color: ${(p) => p.theme.gray};
  transform: ${(p) => (p.$expanded ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s ease;
  flex-shrink: 0;
`

const FormSection = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Label = styled.label`
  font-size: 13px;
  color: ${(p) => p.theme.gray};
  font-family: 'NunitoSansSemiBold';
`

const Input = styled.input`
  outline: none;
  font-size: 15px;
  padding: 10px 12px;
  border: none;
  border-radius: ${(p) => p.theme.radius};
  color: ${(p) => p.theme.white};
  caret-color: ${(p) => p.theme.white};
  background-color: ${(p) => p.theme.input.default};
  height: 44px;
  box-sizing: border-box;
  width: 100%;

  &:hover,
  &:focus {
    background-color: ${(p) => p.theme.input.basic};
    box-shadow: 0 0 0 2px ${(p) => p.theme.accent};
  }

  &::placeholder {
    color: ${(p) => p.theme.listDesc};
    font-size: 14px;
  }

  &:disabled {
    background-color: ${(p) => p.theme.input.disabled};
    opacity: 0.7;
    &:hover,
    &:focus {
      box-shadow: none;
    }
  }
`

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;

  @media (max-width: ${screenSizes.XS}px) {
    flex-direction: column;
  }
`

const ConnectButton = styled.button`
  padding: 10px 24px;
  border: none;
  border-radius: ${(p) => p.theme.radius};
  font-size: 14px;
  font-family: 'NunitoSansBold';
  cursor: pointer;
  color: ${(p) => p.theme.white};
  height: 44px;
  background-color: ${(p) => p.theme.button.primary.default};

  &:hover {
    background-color: ${(p) => p.theme.button.primary.hover};
  }

  &:disabled {
    background-color: ${(p) => p.theme.button.primary.disabled};
    cursor: not-allowed;
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px ${(p) => p.theme.white}, 0 0 0 4px ${(p) => p.theme.accent};
    outline: none;
  }
`

const DisconnectButton = styled.button`
  padding: 10px 24px;
  border: none;
  border-radius: ${(p) => p.theme.radius};
  font-size: 14px;
  font-family: 'NunitoSansBold';
  cursor: pointer;
  color: ${(p) => p.theme.white};
  height: 44px;
  background-color: ${(p) => p.theme.button.cancel.default};

  &:hover {
    background-color: ${(p) => p.theme.button.cancel.hover};
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px ${(p) => p.theme.accent};
    outline: none;
  }
`

const ErrorBanner = styled.div`
  padding: 8px 12px;
  border-radius: ${(p) => p.theme.radius};
  background-color: ${(p) => p.theme.error};
  color: ${(p) => p.theme.white};
  font-size: 13px;
  font-family: 'NunitoSansSemiBold';
  word-break: break-word;
`

function statusText(status: ConnectionStatus, url: string): string {
  switch (status) {
    case 'connected':
      return `Connected to ${url}`
    case 'connecting':
      return 'Connecting\u2026'
    case 'error':
      return 'Connection error'
    default:
      return 'Disconnected'
  }
}

export function ConnectionCard({
  connectionStatus,
  gatewayUrl,
  token,
  password,
  errorMessage,
  setGatewayUrl,
  setToken,
  setPassword,
  connect,
  disconnect,
}: ConnectionCardProps) {
  const isConnected = connectionStatus === 'connected'
  const isConnecting = connectionStatus === 'connecting'
  const inputsDisabled = isConnected || isConnecting
  const [expanded, setExpanded] = useState(!isConnected)

  const handleToggle = () => {
    if (isConnected) setExpanded((prev) => !prev)
  }

  const handleDisconnect = () => {
    disconnect()
    setExpanded(true)
  }

  const showForm = isConnected ? expanded : true
  const Dot = isConnecting ? PulsingDot : StatusDot

  return (
    <Card>
      <CardHeader $clickable={isConnected} onClick={handleToggle}>
        <StatusRow>
          <Dot $status={connectionStatus} />
          <StatusLabel>{statusText(connectionStatus, gatewayUrl)}</StatusLabel>
        </StatusRow>
        {isConnected && <ExpandIcon $expanded={expanded}>&#9660;</ExpandIcon>}
      </CardHeader>

      {showForm && (
        <FormSection>
          <FieldGroup>
            <Label>Gateway URL</Label>
            <Input
              type="text"
              value={gatewayUrl}
              placeholder="ws://localhost:18789"
              onChange={(e) => setGatewayUrl(e.target.value)}
              disabled={inputsDisabled}
            />
          </FieldGroup>

          <FieldGroup>
            <Label>Token (optional)</Label>
            <Input
              type="password"
              value={token}
              placeholder="Gateway auth token"
              onChange={(e) => setToken(e.target.value)}
              disabled={inputsDisabled}
            />
          </FieldGroup>

          <FieldGroup>
            <Label>Password (optional)</Label>
            <Input
              type="password"
              value={password}
              placeholder="Gateway password"
              onChange={(e) => setPassword(e.target.value)}
              disabled={inputsDisabled}
            />
          </FieldGroup>

          {errorMessage && <ErrorBanner>{errorMessage}</ErrorBanner>}

          <ButtonRow>
            {isConnected ? (
              <DisconnectButton onClick={handleDisconnect}>Disconnect</DisconnectButton>
            ) : (
              <ConnectButton onClick={connect} disabled={isConnecting || !gatewayUrl.trim()}>
                {isConnecting ? 'Connecting\u2026' : 'Connect'}
              </ConnectButton>
            )}
          </ButtonRow>
        </FormSection>
      )}
    </Card>
  )
}
